import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionHandler,
  Create,
  EmptyStation,
  MessageBar,
  type Nullable,
  type ObjectSchemaDefinition,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field, type FormikValues } from 'formik';
import generator from 'generate-password-browser';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { PasswordPolicy, charsets } from 'password-sheriff';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  CreateIdpUserDocument,
  type CreateIdpUserMutation,
  type CreateIdpUserMutationVariables,
} from '../../../../generated/axauth-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';

// Changes to the password policy should be reflected in password-policy validation error message below
const passwordPolicy = new PasswordPolicy({
  length: { minLength: 10 },
  containsAtLeast: {
    atLeast: 3,
    expressions: [
      charsets.lowerCase,
      charsets.upperCase,
      charsets.numbers,
      charsets.specialCharacters,
    ],
  },
});

type FormData = Nullable<CreateIdpUserMutationVariables['input']['idpUser']>;

export const userCreateSchema = Yup.object<ObjectSchemaDefinition<FormData>>({
  firstName: Yup.string().label('First Name').max(200),
  lastName: Yup.string().label('Last Name').max(200),
  email: Yup.string().label('Email').required().email().max(255),
  passwordHash: Yup.string()
    .label('Password')
    .required()
    .min(10)
    .max(32)
    .test(
      'password-policy',
      // Changes to the following error message should be reflected in the password policy above
      'Password should contain at least 3 of the following 4 types of characters: lower case letters, upper case letters, numbers and special characters',
      function (value: string | undefined) {
        return value !== undefined && passwordPolicy.check(value);
      },
    ),
  idpUserStoreId: Yup.string(),
});

type SubmitResponse = Nullable<CreateIdpUserMutation['createIdpUser']>;

export const UserCreate: React.FC = () => {
  const initialFormValues = useMemo(() => {
    const generateRandomPassword = (): string => {
      return generator.generate({
        length: 12,
        numbers: true,
        lowercase: true,
        uppercase: true,
        strict: true,
      });
    };

    const initialFormValues: FormData = {
      firstName: '',
      lastName: '',
      email: '',
      idpUserStoreId: '',
      passwordHash: generateRandomPassword(),
    };

    return initialFormValues;
  }, []);

  const { envId, userStoreId } = useParams<{
    envId: string;
    userStoreId: string;
  }>();

  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  // Check the status of axauth-service
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );
        setServiceStatusMessage(statusMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const saveData = useCallback(
    async (formData: FormikValues): Promise<SubmitResponse> => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );

      const response = (
        await envApolloClient.mutate<
          CreateIdpUserMutation,
          CreateIdpUserMutationVariables
        >({
          mutation: CreateIdpUserDocument,
          variables: {
            input: {
              idpUser: {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                passwordHash: formData.passwordHash,
                emailVerified: true,
                idpUserStoreId: userStoreId,
              },
            },
          },
          fetchPolicy: 'no-cache',
        })
      ).data?.createIdpUser;

      return response ?? null;
    },
    [envId, userStoreId],
  );

  const history = useHistory();
  const onProceed = useCallback<ActionHandler<FormData, SubmitResponse>>(
    ({ submitResponse }) => {
      const user = submitResponse?.idpUser;
      if (user) {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/provisionedUsers/${user.id}`,
        );
      } else {
        // The schema has the response.data properties marked as optional, since theoretically a user could have
        // permissions to mutate but not to read. In practice this can not happen on that service, so we just throw
        // an error in case we get there.
        throw new Error('Not expected');
      }
    },
    [envId, history, userStoreId],
  );

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="New AxAuth User Account">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Create<FormData, SubmitResponse>
          title="New AxAuth User Account"
          validationSchema={userCreateSchema}
          saveData={saveData}
          onProceed={onProceed}
          cancelNavigationUrl={`/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/provisionedUsers`}
          initialData={{
            data: initialFormValues,
            loading,
          }}
        >
          <Field name="firstName" label="First Name" as={SingleLineTextField} />
          <Field name="lastName" label="Last Name" as={SingleLineTextField} />
          <Field name="email" label="Email Address" as={SingleLineTextField} />
          <Field
            name="passwordHash"
            label="Password"
            as={SingleLineTextField}
          />
        </Create>
      )}
    </>
  );
};

export const UserCreateCrumb: BreadcrumbResolver = () => 'New User';
